import { Trans } from '@lingui/react/macro';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { Mail01 } from '@untitledui/icons/Mail01';
import { UserEdit } from '@untitledui/icons/UserEdit';

import type {
  PrequalificationResultDto,
  ProjectDisqualificationReason,
} from '@/api/core-api/types.gen';

import { BaseBanner } from '@/components/base/banner/banner';
import { Button, LinkButton } from '@/components/base/buttons/button';
import { BASE_PATH } from '@/constants/config';

function MustCompleteProfileBanner() {
  return (
    <BaseBanner
      title={<Trans>Complete your profile settings to apply</Trans>}
      icon={
        <div className="flex size-10 items-center justify-center rounded-full bg-bg-brand-primary_alt text-display-xs lg:size-14 lg:text-display-md">
          💎
        </div>
      }
      message={
        <Trans>
          Add the missing details in your profile settings so you can apply for
          this project.
        </Trans>
      }
      action={
        <div className="flex flex-row items-start">
          <LinkButton
            to="/participants/settings"
            search={{ tab: 'profile-info' }}
            size="sm"
            color="secondary"
            className="mt-2 overflow-auto"
            iconLeading={UserEdit}
          >
            <Trans>Complete profile</Trans>
          </LinkButton>
        </div>
      }
    />
  );
}

function WorkEmailNeedsReverificationBanner({
  onVerifyWorkEmail,
}: {
  onVerifyWorkEmail?: () => void;
}) {
  return (
    <BaseBanner
      title={<Trans>Re-verify your work email to apply</Trans>}
      message={
        <Trans>
          Your work email verification has expired. Re-verify your email to
          continue to access high-incentive projects that require your
          professional background.
        </Trans>
      }
      icon={
        <img
          src={`${BASE_PATH}/group-avatar.png`}
          alt=""
          aria-hidden="true"
          className="size-12 shrink-0 object-contain lg:h-14 lg:w-20.5"
        />
      }
      action={
        <div className="flex flex-row items-start">
          {onVerifyWorkEmail ? (
            <Button
              size="sm"
              color="secondary"
              className="mt-2"
              iconLeading={Mail01}
              onClick={onVerifyWorkEmail}
            >
              <Trans>Verify work email</Trans>
            </Button>
          ) : (
            <LinkButton
              to="/participants/settings"
              search={{ tab: 'professional-background' }}
              size="sm"
              color="secondary"
              className="mt-2"
              iconLeading={Mail01}
            >
              <Trans>Verify work email</Trans>
            </LinkButton>
          )}
        </div>
      }
    />
  );
}

function WorkEmailRequiredBanner({
  onVerifyWorkEmail,
}: {
  onVerifyWorkEmail?: () => void;
}) {
  return (
    <BaseBanner
      title={<Trans>Verify your work email to apply</Trans>}
      icon={
        <img
          src={`${BASE_PATH}/group-avatar.png`}
          alt=""
          aria-hidden="true"
          className="size-12 shrink-0 object-contain lg:h-14 lg:w-20.5"
        />
      }
      message={
        <Trans>
          This project requires a verified work email to confirm your
          employment. We&apos;ll only use it for verification and won&apos;t
          contact you through it.
        </Trans>
      }
      action={
        <div className="flex flex-row items-start">
          {onVerifyWorkEmail ? (
            <Button
              size="sm"
              color="secondary"
              className="mt-2"
              iconLeading={Mail01}
              onClick={onVerifyWorkEmail}
            >
              <Trans>Verify work email</Trans>
            </Button>
          ) : (
            <LinkButton
              to="/participants/settings"
              search={{ tab: 'professional-background' }}
              size="sm"
              color="secondary"
              className="mt-2"
              iconLeading={Mail01}
            >
              <Trans>Verify work email</Trans>
            </LinkButton>
          )}
        </div>
      }
    />
  );
}

function TrustedCompanyCheckPendingBanner() {
  return (
    <BaseBanner
      title={<Trans>Confirming your details</Trans>}
      icon={
        <img
          src={`${BASE_PATH}/group-avatar.png`}
          alt=""
          aria-hidden="true"
          className="size-12 shrink-0 object-contain lg:h-14 lg:w-20.5"
        />
      }
      message={
        <Trans>
          Your work email is verified. We&apos;re quickly confirming your
          company details so you can apply to this project.
        </Trans>
      }
    />
  );
}

function IncorrectDemographicsBanner() {
  return (
    <BaseBanner
      variant="warning"
      icon={
        <div className="flex size-10 items-center justify-center rounded-full bg-warning-secondary text-display-xs lg:size-14 lg:text-4xl">
          😔
        </div>
      }
      title={<Trans>This project isn&apos;t the right fit</Trans>}
      message={
        <Trans>
          This study requires participants with certain demographic details, and
          your profile doesn&apos;t quite match. Don&apos;t worry, there are
          plenty of other projects that might be perfect for you.
        </Trans>
      }
      action={
        <div className="flex flex-row items-start">
          <LinkButton
            color="secondary"
            to="/participants/projects"
            className="mt-2"
          >
            <Trans>See other projects</Trans>
          </LinkButton>
        </div>
      }
    />
  );
}

function PastParticipationBanner() {
  return (
    <BaseBanner
      variant="warning"
      icon={
        <div className="flex size-10 items-center justify-center rounded-full bg-warning-secondary text-display-xs lg:size-14 lg:text-4xl">
          ⏳
        </div>
      }
      title={
        <Trans>You&apos;ve participated with this researcher recently</Trans>
      }
      message={
        <Trans>
          This researcher is looking for participants they haven&apos;t worked
          with recently, so you&apos;re not eligible this time. You can still
          earn by referring others to this project using your referral link
          below.
        </Trans>
      }
      action={
        <div className="flex flex-row items-start">
          <LinkButton
            color="secondary"
            to="/participants/projects"
            className="mt-2"
          >
            <Trans>See other projects</Trans>
          </LinkButton>
        </div>
      }
    />
  );
}

function DefaultDisqualificationBanner() {
  return (
    <BaseBanner
      icon={<AlertCircle className="text-warning size-10 shrink-0" />}
      title={<Trans>You are not eligible for this project</Trans>}
      message={<Trans>You are not eligible for this project.</Trans>}
      action={
        <div className="flex flex-row items-start">
          <LinkButton
            to="/participants/projects"
            size="sm"
            color="secondary"
            className="mt-4"
          >
            <Trans>Browse other projects</Trans>
          </LinkButton>
        </div>
      }
    />
  );
}

const REASON_BANNERS = {
  mustCompleteProfile: MustCompleteProfileBanner,
  trustedCompanyCheckPending: TrustedCompanyCheckPendingBanner,
  incorrectDemographics: IncorrectDemographicsBanner,
  pastParticipation: PastParticipationBanner,
  projectMatchGracePeriod: IncorrectDemographicsBanner,
  // Quotas V2: every bucket this profile could fill is full for now.
  quotaSegmentFull: DefaultDisqualificationBanner,
} satisfies Record<
  Exclude<
    ProjectDisqualificationReason,
    'workEmailRequired' | 'workEmailNeedsReverification'
  >,
  React.ComponentType
>;

function getDisqualificationBanner(
  reason: ProjectDisqualificationReason | undefined,
): React.ComponentType {
  if (
    !reason ||
    reason === 'workEmailRequired' ||
    reason === 'workEmailNeedsReverification'
  ) {
    return DefaultDisqualificationBanner;
  }

  // A reason the API added before this client learned it must still render:
  // an undefined component here throws on the details page.
  return REASON_BANNERS[reason] ?? DefaultDisqualificationBanner;
}

interface PrequalificationBannerProps {
  prequalifies: PrequalificationResultDto;
  onVerifyWorkEmail?: () => void;
}

export function PrequalificationBanner({
  prequalifies,
  onVerifyWorkEmail,
}: PrequalificationBannerProps) {
  if (prequalifies.disqualificationReason === 'workEmailNeedsReverification') {
    return (
      <WorkEmailNeedsReverificationBanner
        onVerifyWorkEmail={onVerifyWorkEmail}
      />
    );
  }

  if (prequalifies.disqualificationReason === 'workEmailRequired') {
    return <WorkEmailRequiredBanner onVerifyWorkEmail={onVerifyWorkEmail} />;
  }

  const Banner = getDisqualificationBanner(prequalifies.disqualificationReason);

  return <Banner />;
}
